import React, {useEffect, useState} from 'react';
import axios, {AxiosError} from 'axios';
import {Heading, Skeleton, Text} from 'wix-style-react';

import {Footer, Header, types} from '@wix/marketing-header-and-footer-react';
import {useTranslate} from '@wix/premium-bricks-translate';

import {useServices} from '../../providers/ServicesContext';
import {getUrlParameterValue} from '../../utils/getUrlParameterValue';
import {WhoisLookupParams, WhoisLookupResponse, WhoisWebLookupEndpoint} from '../../WhoisWeb';
import {InfoPlaceholder} from '../InfoPlaceholder';
import {WhoisForm} from '../WhoisForm';
import {WhoisInfo} from '../WhoisInfo';

import s from './App.scss';

export interface AppViewProps {
  dataHook?: string;
  reCaptchaSiteKey: string;
  locale: string;
  isAuthorized: boolean;
}

export interface AppViewState {
  isLoaded: boolean;
  isLoading: boolean;
  isError: boolean;
  whoisInfo: null | string;
  domainName: null | string;
  isDomainAvailable: boolean;
}

export const App = (props: AppViewProps) => {
  const {t, tReady} = useTranslate();
  const {fedopsLoggerService, biLoggerService} = useServices();
  const [state, setState] = useState<AppViewState>({
    isLoaded: false,
    isLoading: false,
    isError: false,
    whoisInfo: null,
    domainName: null,
    isDomainAvailable: false,
  });

  useEffect(() => {
    fedopsLoggerService.appLoaded();
    biLoggerService.biLogger
      .whoisPageView({
        referralAdditionalInfo: getUrlParameterValue('referralAdditionalInfo'),
      })
      .catch((error: Error) => window.Sentry.captureException(error));
  }, [biLoggerService, fedopsLoggerService]);

  const {dataHook, reCaptchaSiteKey, isAuthorized, locale} = props;

  const getHeaderAndFooterConfig = (): types.Config => {
    return {
      language: locale,
      postLogin: location.href,
      postSignUp: location.href,
      languageRedirectUrl: () => 'https://whois.wix.com',
    }; // should return Config object: https://github.com/wix-private/marketing-header-and-footer/#configuration-options
  };

  const handleSubmit = async (data: WhoisLookupParams) => {
    setState((currentState) => ({...currentState, isLoading: true}));

    biLoggerService.biLogger
      .whoisClickSubmit({
        domain: data.domainName,
        referralAdditionalInfo: getUrlParameterValue('referralAdditionalInfo'),
      })
      .catch((error: Error) => window.Sentry.captureException(error));

    try {
      const response = await axios.get<WhoisLookupResponse>(WhoisWebLookupEndpoint, {params: data});

      setState((currentState) => ({
        ...currentState,
        isLoading: false,
        isLoaded: true,
        isError: false,
      }));

      const isValidResponse = response.status === 200 && response.data;

      if (isValidResponse && typeof response.data.body === 'string') {
        setState((currentState) => ({
          ...currentState,
          whoisInfo: response.data.body as string,
          domainName: data.domainName,
          isDomainAvailable: response.data.available,
        }));

        biLoggerService.biLogger
          .whoisClickSubmitResult({
            domain: data.domainName,
            result: 'success',
            resultCode: String(response.status),
            referralAdditionalInfo: getUrlParameterValue('referralAdditionalInfo'),
          })
          .catch((error: Error) => window.Sentry.captureException(error));
      } else {
        setState((currentState) => ({
          ...currentState,
          isError: true,
          whoisInfo: null,
          domainName: null,
        }));

        biLoggerService.biLogger
          .whoisClickSubmitResult({
            domain: data.domainName,
            result: 'failure',
            resultCode: String(response.status),
            referralAdditionalInfo: getUrlParameterValue('referralAdditionalInfo'),
          })
          .catch((error: Error) => window.Sentry.captureException(error));
      }
    } catch (error: any) {
      const err: AxiosError = error;

      setState((currentState) => ({
        ...currentState,
        isLoading: false,
        isLoaded: true,
        whoisInfo: null,
        domainName: null,
        isError: true,
      }));

      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx

        biLoggerService.biLogger
          .whoisClickSubmitResult({
            domain: data.domainName,
            result: 'failure',
            resultCode: String(err.response.status),
            referralAdditionalInfo: getUrlParameterValue('referralAdditionalInfo'),
          })
          .catch((e: Error) => window.Sentry.captureException(e));
      } else {
        window.Sentry.captureException(err);
      }
    }
  };

  if (!tReady) {
    return null;
  }

  if (!t) {
    throw new Error('t is not defined');
  }

  const {isLoaded, isLoading, isError, whoisInfo, domainName, isDomainAvailable} = state;

  return (
    <div className={s.root} data-hook={dataHook}>
      <Header config={getHeaderAndFooterConfig()} />

      <section className={s.main}>
        <div className={s.content}>
          <div className={s.form}>
            <div className={s.titleWrapper}>
              <Heading dataHook="main-title" appearance="H1">
                {t('whois.title')}
              </Heading>
            </div>

            <div className={s.subTitleWrapper}>
              <Text dataHook="sub-title" weight="thin" size="medium">
                {t('whois.sub_title')}
              </Text>
            </div>

            <div className={s.formWrapper}>
              <WhoisForm
                isAuthorized={isAuthorized}
                isLoading={isLoading}
                dataHook="whois-form"
                reCaptchaSiteKey={reCaptchaSiteKey}
                onSubmit={handleSubmit}
              />
            </div>

            <Text weight="normal" size="small">
              {t({
                id: 'whois.more_info_link',
                components: [
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href={`https://support.wix.com/articles/id/45d91da2-5900-4917-a1b4-1c16f557307c?referralAdditionalInfo=WHOISDomainSearch&locale=${locale}`}
                    rel="noopener noreferrer"
                    target="_blank"
                    key={0}
                  />,
                ],
              })}
            </Text>
          </div>

          <div className={s.result}>
            <div className={`${s.resultContainer} ${isLoading ? s.loading : ''}`}>
              {isLoaded && <hr className={s.separator} />}

              {isLoading ? (
                <>
                  <div className={s.skeletonWrapper}>
                    <Skeleton
                      spacing="small"
                      content={[
                        {size: 'medium', type: 'line'},
                        {size: 'full', type: 'line'},
                        {size: 'full', type: 'line'},
                      ]}
                    />
                  </div>
                  <div className={s.skeletonWrapper}>
                    <Skeleton
                      spacing="small"
                      content={[
                        {size: 'medium', type: 'line'},
                        {size: 'full', type: 'line'},
                        {size: 'full', type: 'line'},
                      ]}
                    />
                  </div>
                  <div className={s.skeletonWrapper}>
                    <Skeleton
                      spacing="small"
                      content={[
                        {size: 'medium', type: 'line'},
                        {size: 'full', type: 'line'},
                        {size: 'full', type: 'line'},
                      ]}
                    />
                  </div>
                </>
              ) : (
                <>
                  {isLoaded && whoisInfo && !isDomainAvailable && (
                    <WhoisInfo dataHook="whois-info" domainName={domainName} whoisInfo={whoisInfo} />
                  )}

                  {(!isLoaded || !whoisInfo || isDomainAvailable) && (
                    <InfoPlaceholder
                      dataHook="whois-info-placeholder"
                      isAuthorized={isAuthorized}
                      isLoaded={isLoaded}
                      isError={isError}
                      isAvailable={isDomainAvailable}
                      domainName={domainName}
                      whoisInfo={whoisInfo}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
